import { useEffect, useRef, useState, memo } from 'react';
import { message as toast, Tooltip } from 'antd';
import CollectAction from '../collect-action';
import DetailSkeleton from './detail-skeleton';
import QaRecords, { IQaRecordItem } from '@/containers/qa-drawer/components/qa-records';
import { CloseOutlined } from '@ant-design/icons';
import { toastInQa } from '@/utils/qa';
import { getAnswerDetail, qa_pair_detail } from '@/apis';
import {
    transformToQaRecordsByOldQaDetail,
    transformToQaRecordsByPairOfQaDetail,
} from '@/containers/qa-drawer/components/dialogue-area/index.helper';
import { FullScreen, ExitFullScreen } from '@/components/svg-icons/navigation-bar';
import queryDomMethods from '@/utils/query-dom-methods';
import { useGlobalStore } from '@/store/global';
import { useIsFullScreen } from '../drag-drawer/index.helper';
import './index.scss';

type TDataType = 'gbot' | 'pair_of_qa';

interface IProps {
    // 数据类型
    type: TDataType;
    // 报错时是否需要使用Qa组件内的toast组件
    needToastInQa?: boolean;
    id?: any;
    onClose?: () => void;
}

const QuestionDetail = (props: IProps) => {
    const { type, id: detailId, needToastInQa, onClose } = props;
    const { setDragDrawerWidthForMax, setDragDrawerWidthForMin } = useGlobalStore();
    const scrollDom = useRef<HTMLDivElement | null>(null);
    const [detail, setDetail] = useState<{
        projectSubjectName?: string;
        summary?: string;
        initState?: boolean;
        askId?: number;
        qaId?: string;
    }>();
    const [records, setRecords] = useState<IQaRecordItem[]>([]);
    const isFullScreen = useIsFullScreen();
    const qaFlashIndex = useRef(0);

    const handleInitError = (message: string) => {
        setTimeout(() => {
            onClose?.();
            needToastInQa ? toastInQa(message) : toast.warning(message);
        }, 500);
    };

    const initPairOfQaDetail = async (detailId: string) => {
        const { result, message = '网络开了点儿小差～' } = await qa_pair_detail({ qaId: detailId });
        if (!result) {
            handleInitError(message);
            return;
        }
        const { flashIndex, records } = transformToQaRecordsByPairOfQaDetail(result);
        qaFlashIndex.current = flashIndex;
        setDetail({ initState: result.collectFlag, qaId: detailId });
        setRecords(records);
    };

    const initGbotQaDetail = async (detailId: number) => {
        const { result, message = '网络开了点儿小差～' } = await getAnswerDetail(detailId);
        if (!result) {
            handleInitError(message);
            return;
        }
        const { askCollect, projectName, subjectName, answer } = result;
        setDetail({
            initState: askCollect?.is_ask_collect === 1,
            projectSubjectName: `${projectName}-${subjectName}`,
            summary: answer?.[0]?.question_content,
            askId: detailId,
        });
        setRecords(transformToQaRecordsByOldQaDetail(result));
    };

    const getScrollHeight = (flashIndex: number) => {
        const questioners = scrollDom.current!.querySelectorAll('.questioner-container');
        const respondents = scrollDom.current!.querySelectorAll('.respondent-container');
        let scrollTop = 0;
        for (let i = 0; i < questioners.length; i++) {
            if (i >= flashIndex) return scrollTop;
            // console.log(`questioners-${i}-${questioners[i]?.clientHeight}`);
            // console.log(`respondents-${i}-${respondents[i]?.clientHeight}`);
            scrollTop += (questioners[i]?.clientHeight || 0) + (respondents[i]?.clientHeight || 0) + 48;
        }
        return scrollTop;
    };

    const handleScrollToFlashData = () => {
        if (!qaFlashIndex.current) return;
        scrollDom.current?.scrollTo({ top: getScrollHeight(qaFlashIndex.current) });
    };

    const handleClose = () => {
        onClose?.();
        setTimeout(() => {
            setRecords([]);
            setDetail(undefined);
        }, 500);
    };

    useEffect(() => {
        if (records.length) {
            type === 'pair_of_qa'
                ? handleScrollToFlashData()
                : setTimeout(() => scrollDom.current?.scrollTo({ top: scrollDom.current!.scrollHeight, behavior: 'smooth' }), 100);
        }
    }, [records]);

    useEffect(() => {
        if (!detailId) return;
        if (type === 'pair_of_qa') {
            initPairOfQaDetail(detailId);
        } else if (type === 'gbot') {
            initGbotQaDetail(detailId);
        }
    }, [detailId, type]);

    return detail ? (
        <div className="qa-old-question">
            <div className="title-box">
                <div>问题详情</div>
                <CollectAction initState={detail.initState} askId={detail.askId} qaId={detail.qaId} needToastInQa />
                <Tooltip
                    title={isFullScreen ? '退出全屏' : '全屏'}
                    placement="bottomRight"
                    arrow={{ pointAtCenter: true }}
                    getPopupContainer={queryDomMethods.queryLucaQaRoot}
                    autoAdjustOverflow={false}
                >
                    <div className="navigation-bar__size-control">
                        {isFullScreen ? (
                            <ExitFullScreen onClick={setDragDrawerWidthForMin} />
                        ) : (
                            <FullScreen onClick={setDragDrawerWidthForMax} />
                        )}
                    </div>
                </Tooltip>
                <div className="title-box__close-container" onClick={handleClose}>
                    <CloseOutlined className="title-box__close" />
                </div>
            </div>
            {detail.projectSubjectName && (
                <div className="question-info-box">
                    <span>{detail.projectSubjectName}</span>
                    <span>{detail.summary}</span>
                </div>
            )}
            <div className="old-qa-records-container" ref={scrollDom}>
                <QaRecords list={records} />
            </div>
        </div>
    ) : (
        <DetailSkeleton />
    );
};

export default memo(QuestionDetail);
